/**
 * The roster screen's own domain logic — Task T2-E-web — kept out of the components
 * so the week arithmetic, the warning presentation table and the assign-request
 * builder are testable without mounting anything, the same split `people/status.ts`
 * and `permissions/grants.ts` each keep for their own screen.
 *
 * ── Warnings are information, not judgement ──────────────────────────────────
 *
 * The brief (research pass 4, findings 7–10): fatigue/cap breaches are SOFT and never
 * block; the chip labels below are neutral wording, never "vi phạm". `Severity` has
 * no literal `'warning'` value — the type is `'critical' | 'high' | 'medium' | 'low' |
 * 'info'` (`states/icons.tsx`) — so `medium` is the closest non-red tone (its token is
 * a gold/amber, `tokens.css`'s `--wp-color-sev-medium`, never the red `critical`/
 * `high` pair) and is what every soft code below reads. `night_work` alone reads
 * `info`, per the brief.
 */

import { isoWeekdayOf } from '../attendance/month';
import { addDays } from '../i18n/datetime';
import type { MessageKey } from '../i18n/messages';
import type { IconName, Severity } from '../states/icons';
import type {
  LabourCapWarning,
  LabourCapWarningCode,
  Shift,
  ShiftAssignmentCreateRequest,
  ShiftRosterVia,
  ShiftScopeType,
} from '../api/roster';
import type { WorkforceApiError } from '../api/errors';

/* ──────────────────────────────────────────────────────────────── week math ── */

/**
 * The occurrence of ISO weekday `dow` (Monday is 1 … Sunday is 7) on or before
 * `date`. Pure arithmetic on the calendar digits, the same `isoWeekdayOf`/`addDays`
 * pair `attendance/month.ts` uses for its own Monday-first grid, and for the
 * identical reason: no `Date` object reads a time zone here, because the input is a
 * local date with no time of day.
 */
export function weekStartOnOrBefore(date: string, dow: number): string {
  const weekday = isoWeekdayOf(date);
  const delta = (weekday - dow + 7) % 7;
  return addDays(date, -delta);
}

/**
 * `weekStartOnOrBefore(date, 1)` — this screen's own default `dow` when nothing has
 * corrected it yet. Kept as its own function because "Monday" is the fallback this
 * screen actually starts from (`RosterScreen.tsx`'s own header), not an arbitrary
 * instance of the general one above.
 */
export function mondayOnOrBefore(date: string): string {
  return weekStartOnOrBefore(date, 1);
}

/** Move a `week_start` by whole weeks — the prev/next/this-week controls. */
export function shiftWeek(weekStart: string, deltaWeeks: number): string {
  return addDays(weekStart, deltaWeeks * 7);
}

/** The last of the seven days a `week_start` covers — for the range label. */
export function weekEnd(weekStart: string): string {
  return addDays(weekStart, 6);
}

/* ──────────────────────────────────────── learning week_start_dow from a 400 ── */

/**
 * `SessionUser` publishes no `week_start_dow` field, so `RosterScreen.tsx` guesses
 * Monday and asks `GET /shifts/roster` — the one place the company's real value is
 * knowable at all. A wrong guess comes back `400 WORKFORCE_VALIDATION_FAILED` with
 * `details.issues: [{ field: 'week_start', reason: "…", expected_week_start_dow: N
 * }]` (`openapi.yaml`'s own description of the operation;
 * `src/modules/attendance/service.ts`'s `issue()` helper is what stamps the field
 * alongside the human `reason` prose). `ApiErrorNote` never reads
 * `details.issues[]` (it only ever shows the top-level `message`, which is the same
 * generic "dữ liệu không hợp lệ" sentence for every `WORKFORCE_VALIDATION_FAILED` —
 * `src/lib/errors.ts`), so without this the correction the server actually sent is
 * simply never read by anything.
 *
 * Reads ONLY the typed `expected_week_start_dow` field — never the `reason`
 * sentence. An earlier version of this function regex-matched `ISO weekday (\d)`
 * out of the prose, which meant a reword of that sentence on the server would leave
 * both this file's own test and the server's own integration test green while
 * silently breaking the correction for every non-Monday company — the exact dead
 * end T2-E-web spent two fix rounds removing. `tests/integration/attendance/
 * roster.test.ts` now asserts the field's exact name and value for this reason.
 *
 * `null` for every shape that is not exactly this — a different validation issue, a
 * different code entirely, a missing or out-of-range field — so the caller can tell
 * "learned the real value" from "still a mystery" and fall back to an honest
 * `ApiErrorNote` for the latter rather than mis-parsing silently.
 */
export function weekStartDowFromError(error: WorkforceApiError): number | null {
  if (error.code !== 'WORKFORCE_VALIDATION_FAILED') return null;
  const issues = error.details.issues;
  if (!Array.isArray(issues)) return null;
  for (const entry of issues) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    if (record.field !== 'week_start') continue;
    const dow = record.expected_week_start_dow;
    if (typeof dow === 'number' && Number.isInteger(dow) && dow >= 1 && dow <= 7) return dow;
  }
  return null;
}

const WEEK_START_DOW_STORAGE_PREFIX = 'wp.roster.weekStartDow.';

/**
 * A **preference learned from the server's own refusal**, not a credential — the
 * same "survives a reload, discloses nothing" reasoning `i18n/index.tsx`'s own
 * `readStoredLocale`/`storeLocale` state for the locale choice. Keyed per company,
 * since a session with more than one membership could see a different value in each.
 * Wrapped in `try`/`catch` for the identical reason: private mode, disabled storage,
 * or a test environment without it must not turn a convenience into a crash.
 */
export function readStoredWeekStartDow(companyId: string): number | null {
  try {
    const raw = window.localStorage.getItem(`${WEEK_START_DOW_STORAGE_PREFIX}${companyId}`);
    const parsed = raw === null ? Number.NaN : Number(raw);
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 7 ? parsed : null;
  } catch {
    return null;
  }
}

export function storeWeekStartDow(companyId: string, dow: number): void {
  try {
    window.localStorage.setItem(`${WEEK_START_DOW_STORAGE_PREFIX}${companyId}`, String(dow));
  } catch {
    /* see readStoredWeekStartDow */
  }
}

/* ──────────────────────────────────────────────────────────── minutes → hh:mm ── */

/**
 * `week_planned_minutes` as `"hh:mm"` — a literal zero-padded clock reading, per the
 * brief, and deliberately **not** `timer/elapsed.ts`'s own `formatDurationMinutes`
 * ("X giờ Y phút"): that wording is right for a duration read out loud, and wrong for
 * a total meant to line up in a column of a weekly roster.
 *
 * A non-finite or negative input reads `"00:00"` rather than `NaN:NaN` or a negative
 * clock — the same "never invent a number the server did not send" discipline
 * `formatDurationMinutes` itself keeps, applied to this format instead.
 */
export function minutesToHHMM(totalMinutes: number): string {
  const safe = Number.isFinite(totalMinutes) && totalMinutes > 0 ? Math.round(totalMinutes) : 0;
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/* ───────────────────────────────────────────────────────────── warning chips ── */

export interface WarningPresentation {
  readonly severity: Severity;
  readonly icon: IconName;
  readonly labelKey: MessageKey;
}

/**
 * One entry per `LabourCapWarningCode`, exhaustive so a seventh code added to
 * `LABOUR_CAP_WARNING_CODES` (`api/roster.ts`) fails to **compile** here — the same
 * discipline `DeviceBadges.tsx`'s own `STATUS_LABEL` keeps. `overlap` carries an
 * entry only for type completeness: `api/roster.ts`'s own header explains why it
 * can never actually reach this table at runtime, and no screen renders it.
 *
 * That compile-time exhaustiveness is a guarantee about THIS FILE's own closed
 * union, and proves nothing about the wire: `warning.code` at a render site
 * (`WarningChips.tsx`) is a value that came off the network through a type
 * assertion, not a runtime-validated one, so a seventh code the server starts
 * sending before this table is updated is a real, reachable case — indexing this
 * `Record` with it returns `undefined`, not a compile error. `warningPresentationFor`
 * below is how `WarningChip` reads this table for exactly that reason — never a
 * direct index on `warning.code`.
 *
 * Every label below is the brief's own wording, verbatim.
 */
export const LABOUR_CAP_WARNING_PRESENTATION: Readonly<
  Record<LabourCapWarningCode, WarningPresentation>
> = {
  daily_over_8h: { severity: 'medium', icon: 'clock', labelKey: 'roster.warning.daily_over_8h' },
  weekly_over_48h: {
    severity: 'medium',
    icon: 'hourglass',
    labelKey: 'roster.warning.weekly_over_48h',
  },
  days_over_6_per_week: {
    severity: 'medium',
    icon: 'calendar-check',
    labelKey: 'roster.warning.days_over_6_per_week',
  },
  rest_under_12h: {
    severity: 'medium',
    icon: 'circle-alert',
    labelKey: 'roster.warning.rest_under_12h',
  },
  night_work: { severity: 'info', icon: 'moon', labelKey: 'roster.warning.night_work' },
  // Never rendered — see this table's own header.
  overlap: { severity: 'medium', icon: 'circle-alert', labelKey: 'roster.warning.overlap' },
};

/**
 * The fallback `WarningChip` renders for a `warning.code` this table does not
 * know about — a wire value outside the closed union `LABOUR_CAP_WARNING_PRESENTATION`
 * is typed over, per that table's own header. A generic, still-legible chip rather
 * than a thrown `TypeError` that would take the whole roster grid down over one
 * unrecognised code on one warning, on one cell.
 */
export const UNKNOWN_WARNING_PRESENTATION: WarningPresentation = {
  severity: 'medium',
  icon: 'circle-alert',
  labelKey: 'roster.warning.unknown',
};

/**
 * `WarningChip`'s own lookup — widened to a plain `string` parameter rather than
 * `LabourCapWarningCode`, the same defensive cast `assignmentSkipReasonKey` above
 * uses for the identical reason: a value that came off the network through a type
 * assertion is not a value TypeScript actually checked, so indexing
 * `LABOUR_CAP_WARNING_PRESENTATION` directly with `warning.code` lets the compiler
 * "prove" the result can never be `undefined` — which is exactly the type lie that
 * let a seventh code reach `presentation.severity` and throw. Casting the table to
 * `Partial<Record<string, …>>` here makes the lookup honestly optional, so the `??`
 * fallback is real rather than lint-suppressed.
 */
export function warningPresentationFor(code: string): WarningPresentation {
  const known = (LABOUR_CAP_WARNING_PRESENTATION as Partial<Record<string, WarningPresentation>>)[
    code
  ];
  return known ?? UNKNOWN_WARNING_PRESENTATION;
}

/**
 * The chip's tooltip detail — "{minutes} phút đã xếp" or "{days} ngày đã làm",
 * depending on which field `detail` actually carries for this code
 * (`LabourCapWarning`'s own header: `days` only for `days_over_6_per_week`, `minutes`
 * for the rest). `null` when neither is present, so the caller can omit the `title`
 * attribute entirely rather than render an empty tooltip.
 */
export function warningDetailKey(
  warning: LabourCapWarning,
): { readonly key: MessageKey; readonly args: Readonly<Record<string, number>> } | null {
  if (warning.code === 'days_over_6_per_week' && warning.detail.days !== undefined) {
    return { key: 'roster.warning.detail.days', args: { days: warning.detail.days } };
  }
  if (warning.detail.minutes !== undefined) {
    return { key: 'roster.warning.detail.minutes', args: { minutes: warning.detail.minutes } };
  }
  return null;
}

/**
 * `warnings[]`, deduplicated by `code` — `ShiftRosterEmployee.week_warnings` is
 * already deduplicated server-side, but a day cell's own `warnings[]` is not
 * documented as such, and rendering the same chip twice on one cell would read as two
 * findings where there is one.
 */
export function dedupeWarnings(warnings: readonly LabourCapWarning[]): readonly LabourCapWarning[] {
  const seen = new Set<LabourCapWarningCode>();
  const result: LabourCapWarning[] = [];
  for (const warning of warnings) {
    if (seen.has(warning.code)) continue;
    seen.add(warning.code);
    result.push(warning);
  }
  return result;
}

/* ──────────────────────────────────────────────────────────────── via hints ── */

/**
 * The small "via" hint the grid shows beside a resolved shift, when the rung that
 * answered it is not the employee's own direct assignment — the brief's own examples,
 * "theo phòng ban", "mặc định". `null` for `'employee'` (the ordinary, expected case —
 * no hint) and for `null` (nothing resolved, a different sentence entirely; see
 * `roster.cell.unconfigured`).
 */
const VIA_HINT_KEY: Readonly<Partial<Record<NonNullable<ShiftRosterVia>, MessageKey>>> = {
  job_title: 'roster.via.job_title',
  department: 'roster.via.department',
  branch: 'roster.via.branch',
  company: 'roster.via.company',
  default: 'roster.via.default',
};

export function viaHintKey(via: ShiftRosterVia): MessageKey | null {
  if (via === null || via === 'employee') return null;
  return VIA_HINT_KEY[via] ?? null;
}

/**
 * A shift's own catalogue lookup, `code – name`, or the raw id when the shift is not
 * in the catalogue this screen fetched — a historical assignment naming a shift that
 * was since deactivated is still `GET /shifts`-readable (this screen fetches every
 * shift, not only `active: true`, for exactly this lookup), but a shift outside the
 * caller's company or otherwise absent renders its id honestly rather than nothing,
 * the same "an id outside a page renders honestly" rule `PeopleScreen.tsx`'s own
 * manager-name fallback keeps.
 */
export function shiftLabel(shift: Shift | undefined, shiftId: string): string {
  return shift === undefined ? shiftId : `${shift.code} – ${shift.name}`;
}

/** The scope-type word for the drawer's assignment-list column — every rung, `employee` included. */
export const SCOPE_TYPE_LABEL_KEY: Readonly<Record<ShiftScopeType, MessageKey>> = {
  employee: 'roster.scope.employee',
  job_title: 'roster.scope.job_title',
  department: 'roster.scope.department',
  branch: 'roster.scope.branch',
  company: 'roster.scope.company',
};

/* ──────────────────────────────────────────────────────────── the assign form ── */

/**
 * `AssignmentSkip.reason` of `openapi.yaml` — a machine code, `string` on the wire
 * with no closed enum (`src/modules/attendance/service.ts`'s own `AssignmentSkip`
 * type). Exactly one value the current backend ever produces —
 * `'employee_not_in_company'`, the one `skipped.push` call site in
 * `createShiftAssignments` — transcribed here rather than assumed to be the whole
 * set: the type is open, so a value this table does not know about is a real,
 * reachable case (`assignmentSkipReasonKey`'s own fallback), not a defect in this
 * list.
 */
export const ASSIGNMENT_SKIP_REASON_CODES = ['employee_not_in_company'] as const;
export type AssignmentSkipReasonCode = (typeof ASSIGNMENT_SKIP_REASON_CODES)[number];

const ASSIGNMENT_SKIP_REASON_LABEL_KEY: Readonly<Record<AssignmentSkipReasonCode, MessageKey>> = {
  employee_not_in_company: 'roster.assign.skipped.employee_not_in_company',
};

/**
 * A known code's own sentence, or an honest generic one — **never** the raw
 * machine code (`employee_not_in_company` etc.) rendered as if it were prose. The
 * fallback exists precisely because the wire type has no closed enum: a reason this
 * screen has never seen must still read as a sentence to a Vietnamese-speaking
 * manager, not as a bug report typed in English snake_case.
 */
export function assignmentSkipReasonKey(reason: string): MessageKey {
  const known = (ASSIGNMENT_SKIP_REASON_LABEL_KEY as Partial<Record<string, MessageKey>>)[reason];
  return known ?? 'roster.assign.skipped.unknown';
}

/**
 * The exact `ShiftAssignmentCreateRequest` body "Gán ca" submits, or `null` while a
 * required field (shift, effective-from) is still blank — the drawer's own submit
 * gate, so "can this be sent" and "what would be sent" cannot disagree, the same
 * discipline `permissions/grants.ts`'s own `buildGrantRequest` keeps.
 *
 * `effective_to` is omitted rather than sent blank when the operator left it empty —
 * an open-ended assignment, not a same-day one.
 */
export function buildAssignRequest(draft: {
  readonly employeeId: string;
  readonly shiftId: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
}): ShiftAssignmentCreateRequest | null {
  if (draft.shiftId === '' || draft.effectiveFrom === '') return null;
  return {
    shift_id: draft.shiftId,
    employee_ids: [draft.employeeId],
    effective_from: draft.effectiveFrom,
    ...(draft.effectiveTo === '' ? {} : { effective_to: draft.effectiveTo }),
  };
}
